import React, { FC } from "react";
import {
  LuCheck,
  LuFileText,
  LuInbox,
  LuMail,
  LuMailOpen,
} from "react-icons/lu";
import { useNavigate } from "react-router-dom";

import { KindIcon, Sentence, UnreadDot } from "./NotificationParts";
import { NotificationItem } from "./notifications.types";
import {
  clientLink,
  notificationFullTime,
  notificationText,
  notificationTimeLabel,
  primaryActionLabel,
} from "./notifications.utils";

const MAX_FILE_CHIPS = 3;

const IconButton: FC<{
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ label, onClick, children }) => (
  <button
    type={"button"}
    title={label}
    aria-label={label}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    className={
      "flex h-8 w-8 items-center justify-center rounded-[8px] text-[16px] text-[#6B7185] focus-visible:outline hover:bg-[#F0EDFF] hover:text-[#6B4EFF] focus-visible:outline-2 focus-visible:outline-[#6B4EFF]"
    }>
    {children}
  </button>
);

type Props = {
  item: NotificationItem;
  selected: boolean;
  onSelect: (selected: boolean) => void;
  onOpen: () => void;
  onToggleRead: () => void;
  /** Undefined = Done isn't available yet (database update pending). */
  onToggleDone?: () => void;
};

/** One notification on the Notifications page. */
export const NotificationRow: FC<Props> = ({
  item,
  selected,
  onSelect,
  onOpen,
  onToggleRead,
  onToggleDone,
}) => {
  const navigate = useNavigate();
  const client = clientLink(item);
  const extraFiles = Math.max(0, item.fileCount - MAX_FILE_CHIPS);

  return (
    <div
      role={"button"}
      tabIndex={0}
      aria-label={notificationText(item)}
      data-testid={"notification-row"}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" && e.target === e.currentTarget) onOpen();
      }}
      className={`group relative flex cursor-pointer items-start gap-3 border-b border-[#F1F2F5] px-4 py-[14px] transition-colors last:border-b-0 hover:bg-[#F9F9FB] focus-visible:bg-[#F9F9FB] focus-visible:outline-none ${
        selected ? "bg-[#F7F5FF]" : "bg-white"
      }`}>
      <label
        onClick={(e) => e.stopPropagation()}
        className={`mt-[9px] flex h-4 w-4 flex-none cursor-pointer items-center ${
          selected ? "" : "opacity-0 group-hover:opacity-100"
        } focus-within:opacity-100`}>
        <input
          type={"checkbox"}
          aria-label={"Select notification"}
          checked={selected}
          onChange={(e) => onSelect(e.target.checked)}
          className={"h-4 w-4 cursor-pointer accent-[#6B4EFF]"}
        />
      </label>
      <span className={"mt-[13px]"}>
        <UnreadDot show={!item.isRead} />
      </span>
      <KindIcon category={item.category} />

      <div className={"min-w-0 flex-1"}>
        <Sentence
          item={item}
          className={`block text-[14px] leading-[1.5] ${
            item.isRead ? "opacity-[0.82]" : ""
          }`}
        />

        <div
          className={
            "mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-[#8A90A2]"
          }>
          {item.clientName &&
            (client ? (
              <button
                type={"button"}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(client);
                }}
                title={item.clientName}
                className={
                  "max-w-[320px] truncate rounded-[6px] bg-[#F1F2F5] px-[7px] py-[1px] font-semibold text-[#5B6678] hover:bg-[#E8E8EC] hover:text-[#1F2433]"
                }>
                {item.clientName}
              </button>
            ) : (
              <span
                className={
                  "max-w-[320px] truncate rounded-[6px] bg-[#F1F2F5] px-[7px] py-[1px] font-semibold text-[#5B6678]"
                }>
                {item.clientName}
              </span>
            ))}
          {item.category === "client_uploads" && item.workName && (
            <span>{item.workName}</span>
          )}
        </div>

        {item.category === "client_uploads" && item.files.length > 0 && (
          <div className={"mt-2 flex flex-wrap gap-1.5"}>
            {item.files.slice(0, MAX_FILE_CHIPS).map((file) => (
              <span
                key={file.id}
                className={
                  "inline-flex max-w-[220px] items-center gap-1.5 rounded-[7px] border border-[#E8E8EC] bg-white px-2 py-[3px] text-[12px] font-semibold text-[#3A4256]"
                }>
                <LuFileText className={"flex-none text-[#9097A8]"} />
                <span className={"truncate"}>{file.name}</span>
              </span>
            ))}
            {extraFiles > 0 && (
              <span
                className={
                  "inline-flex items-center rounded-[7px] bg-[#F1F2F5] px-2 py-[3px] text-[12px] font-bold text-[#5B6678]"
                }>
                +{extraFiles} more
              </span>
            )}
          </div>
        )}
        {item.note && (
          <div
            className={
              "mt-2 max-w-[560px] rounded-[9px] bg-[#F7F8FA] px-3 py-2 text-[13px] italic text-[#4A5163]"
            }>
            “{item.note}”
          </div>
        )}
      </div>

      <div className={"flex min-h-[32px] flex-none items-start justify-end"}>
        <span
          title={notificationFullTime(item.date)}
          className={`whitespace-nowrap pt-0.5 text-[12px] tabular-nums group-focus-within:hidden group-hover:hidden ${
            item.isRead
              ? "font-medium text-[#9097A8]"
              : "font-bold text-[#4A5163]"
          }`}>
          {notificationTimeLabel(item.date)}
        </span>
        <div
          className={
            "hidden items-center gap-0.5 group-focus-within:flex group-hover:flex"
          }>
          <button
            type={"button"}
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
            }}
            className={
              "mr-1 h-8 rounded-[8px] border-[1.5px] border-[#DCE2EA] bg-white px-3 text-[12.5px] font-bold text-[#3A4256] hover:border-[#6B4EFF] hover:text-[#6B4EFF]"
            }>
            {primaryActionLabel(item)}
          </button>
          <IconButton
            label={item.isRead ? "Mark as unread" : "Mark as read"}
            onClick={onToggleRead}>
            {item.isRead ? <LuMail /> : <LuMailOpen />}
          </IconButton>
          {onToggleDone && (
            <IconButton
              label={item.isDone ? "Move to inbox" : "Done"}
              onClick={onToggleDone}>
              {item.isDone ? <LuInbox /> : <LuCheck />}
            </IconButton>
          )}
        </div>
      </div>
    </div>
  );
};
